import {
  Box,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Button,
  TextField,
  List,
  ListItem,
  useMediaQuery
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useEffect, useState } from "react";
import axios from 'axios';
import { DateTime } from 'luxon';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';

const GRID_SIZE = 5;
const DEFAULT_TILE_SIZE = 90;

const generateTiles = () => {
  const total = GRID_SIZE * GRID_SIZE;
  const tiles = [...Array(total - 1).keys(), null];
  for (let i = tiles.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [tiles[i], tiles[j]] = [tiles[j], tiles[i]];
  }
  return tiles;
};

const isAdjacent = (index1, index2) => {
  const row1 = Math.floor(index1 / GRID_SIZE);
  const col1 = index1 % GRID_SIZE;
  const row2 = Math.floor(index2 / GRID_SIZE);
  const col2 = index2 % GRID_SIZE;
  return (row1 === row2 && Math.abs(col1 - col2) === 1) || (col1 === col2 && Math.abs(row1 - row2) === 1);
};

const ArrangePuzzleGame = ({ imageSrc, gameId }) => {
  const [tiles, setTiles] = useState([]);
  const [startTime, setStartTime] = useState(null);
  const [moves, setMoves] = useState(0);
  const [rank, setRank] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [userName, setUserName] = useState("");
  const [showNameDialog, setShowNameDialog] = useState(true);
  const [topPlayers, setTopPlayers] = useState([]);
  const [currentPerformance, setCurrentPerformance] = useState(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const TILE_SIZE = isMobile ? 60 : DEFAULT_TILE_SIZE;

  const TILE_IMAGE = imageSrc;

  useEffect(() => {
    fetchToppers();
  }, []);

  const fetchToppers = async (myPerformance = null) => {
    try {
      const res = await axios.get(`/${gameId}.json`);
      const scores = Object.values(res.data || {});
      const sorted = scores.sort((a, b) => a.performance - b.performance);
      setTopPlayers(sorted.slice(0, 3));

      if (myPerformance !== null) {
        const myRank = sorted.findIndex(player =>
          player.userName === userName &&
          player.performance === myPerformance
        );
        setRank(myRank + 1);
      }
    } catch (err) {
      console.error('Error fetching toppers', err);
    }
  };

  useEffect(() => {
    setTiles(generateTiles());
  }, []);

  const startGame = () => {
    const playerName = userName.trim() || "Anonymous";
    setUserName(playerName);
    setTiles(generateTiles());
    setStartTime(Date.now());
    setMoves(0);
    setShowNameDialog(false);
    setShowResult(false);
    setRank(null);
  };

  const handleTileClick = (index) => {
    const emptyIndex = tiles.indexOf(null);
    if (isAdjacent(index, emptyIndex)) {
      const newTiles = [...tiles];
      [newTiles[index], newTiles[emptyIndex]] = [newTiles[emptyIndex], newTiles[index]];
      setTiles(newTiles);
      setMoves(prev => prev + 1);

      if (checkWin(newTiles)) {
        const endTime = Date.now();
        const durationInSeconds = Math.floor((endTime - startTime) / 1000);
        const performance = durationInSeconds * 20 + (moves + 1) * 10;

        setCurrentPerformance(performance);

        const now = DateTime.now().toFormat('dd-MM-yyyy HH:mm:ss');

        const data = {
          userName: userName.trim() || "Anonymous",
          moves: moves + 1,
          timeTaken: durationInSeconds,
          performance,
          time: now,
        };

        axios.post(`/${gameId}.json`, data)
          .then(() => {
            fetchToppers(performance);
            setShowResult(true);
          })
          .catch(err => console.error(err));
      }
    }
  };

  const checkWin = (tiles) => {
    const correct = [...Array(GRID_SIZE * GRID_SIZE - 1).keys()];
    for (let i = 0; i < correct.length; i++) {
      if (tiles[i] !== correct[i]) return false;
    }
    return tiles[tiles.length - 1] === null;
  };

  const handleRestart = () => {
    setTiles(generateTiles());
    setStartTime(Date.now());
    setMoves(0);
    setRank(null);
    setShowResult(false);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
        py: 4,
        mt: '5%',
      }}
    >
      {/* Puzzle Grid */}
      <Box
        sx={{
          width: TILE_SIZE * GRID_SIZE + 4,
          height: TILE_SIZE * GRID_SIZE + 4,
          border: '2px solid #000',
          mb: { xs: 3, md: 0 },
        }}
      >
        <Grid container spacing={0}>
          {tiles.map((tileIndex, i) => {
            const backgroundRow = Math.floor((tileIndex ?? 0) / GRID_SIZE);
            const backgroundCol = (tileIndex ?? 0) % GRID_SIZE;
            return (
              <Grid
                item
                xs={12 / GRID_SIZE}
                key={i}
                onClick={() => handleTileClick(i)}
                sx={{
                  width: TILE_SIZE,
                  height: TILE_SIZE,
                  border: '1px solid #ccc',
                  backgroundColor: tileIndex === null ? '#eee' : 'transparent',
                  backgroundImage: tileIndex !== null ? `url(${TILE_IMAGE})` : 'none',
                  backgroundPosition: `${-backgroundCol * TILE_SIZE}px ${-backgroundRow * TILE_SIZE}px`,
                  backgroundSize: `${GRID_SIZE * TILE_SIZE}px ${GRID_SIZE * TILE_SIZE}px`,
                  cursor: tileIndex !== null ? 'pointer' : 'default',
                }}
              />
            );
          })}
        </Grid>
      </Box>

      {/* Top Players */}
      <Box
        sx={{
          width: '100%',
          maxWidth: 350,
          mt: { xs: 3, md: 4 },
        }}
      >
        <Typography variant="h6" gutterBottom textAlign="center">
          🏆 Top 3 Players
        </Typography>
        <List>
          {topPlayers.map((player, i) => (
            <ListItem key={i} sx={{ justifyContent: 'center' }}>
              <Typography>
                {i + 1}. {player.userName} — Moves: {player.moves}, Time: {player.timeTaken}s
              </Typography>
            </ListItem>
          ))}
        </List>
      </Box>

      {/* Dialogs (unchanged) */}
      <Dialog
        open={showNameDialog}
        onClose={() => setShowNameDialog(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 2,
            position: 'relative',
          },
        }}
      >
        {/* Close Icon at Top-Right Without Hover Circle */}
        <IconButton
          onClick={() => setShowNameDialog(false)}
          aria-label="close"
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            color: (theme) => theme.palette.grey[500],
            backgroundColor: 'transparent',
            '&:hover': {
              backgroundColor: 'transparent',
            },
          }}
        >
          <CloseIcon />
        </IconButton>

        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" fontWeight="bold">
            Enter Your Name
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ pt: 0 }}>
          <TextField
            fullWidth
            label="Your Name"
            placeholder="Leave blank for Anonymous"
            value={userName}
            onChange={(e) => setUserName(e.target.value)}
            sx={{ mt: 1 }}
          />
          <Button
            fullWidth
            variant="contained"
            sx={{
              mt: 3,
              py: 1.2,
              fontWeight: 600,
              textTransform: 'none',
              fontSize: '1rem',
            }}
            onClick={() => {
              if (!userName.trim()) {
                setUserName("Anonymous");
              }
              startGame();
            }}
          >
            Start Game
          </Button>
        </DialogContent>
      </Dialog>




      <Dialog open={showResult} fullWidth maxWidth="xs" PaperProps={{ sx: { p: 2 } }}>
        <DialogTitle>🎉 Puzzle Solved!</DialogTitle>
        <DialogContent>
          <Typography>Moves: {moves}</Typography>
          <Typography>Time Taken: {Math.floor((Date.now() - startTime) / 1000)} sec</Typography>
          <Typography>Rank: {rank ?? 'Calculating...'}</Typography>
          <Button fullWidth variant="contained" sx={{ mt: 2 }} onClick={handleRestart}>
            Play Again
          </Button>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ArrangePuzzleGame;
